import { configureStore } from '@reduxjs/toolkit';
import authReducer, { authBlacklistSync } from 'features/auth/redux/auth-slice';
import { blogsApiSlice } from 'features/blogs/redux/blogs-api-slice';
import charterReducer from 'features/booking/redux/charter-slice';
import logisticsReducer from 'features/booking/redux/logistics-slice';
import transportBookingReducer from 'features/booking/redux/transport-booking-slice';
import { ordersApiSlice } from 'features/orders/redux/order-api-slice';
import { profileApiSlice } from 'features/profile/redux/profile-api-slice';
import { fleetsApiSlice } from 'features/routing/redux/fleets-api-slice';
import { logisticsApiSlice } from 'features/routing/redux/logistics-api-slice';
import { routesAPISlice } from 'features/routing/redux/routes-api-slice';
import { transportAPISlice } from 'features/routing/redux/transport-api-slice';
import settingsReducer from 'features/settings/redux/settings-slice';
import { transactionsApi } from 'features/walletAndTransactions/redux/transactions-api-slice';
import { walletApiSlice } from 'features/walletAndTransactions/redux/wallet-api-slice';
import { layoutAPISlice } from 'features/website-layout/redux/layout-api-slice';
import layoutReducer from 'features/website-layout/redux/layout-slice';
import { combineReducers } from 'redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';
import { createFilter } from 'redux-persist-transform-filter';
import createSagaMiddleware from 'redux-saga';
import {
  Config as SyncConfig,
  createStateSyncMiddleware,
  initMessageListener,
} from 'redux-state-sync';

import rootSaga from './rootSaga';

export const configureStoreWithMiddlewares = (initialState = {}) => {
  const sagaMiddleware = createSagaMiddleware();
  const loadSubsetFilter = createFilter('user', ['userDetails', 'accessToken']);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rootPersistConfig: any = {
    key: 'root',
    storage,
    whitelist: [
      'user',
      'transportBooking',
      'transportCharter',
      'logisticsBooking',
    ],
    transforms: [loadSubsetFilter],
  };

  const stateSyncConfig: SyncConfig = {
    blacklist: [...authBlacklistSync],
  };

  const rootReducer = combineReducers({
    layout: layoutReducer,
    user: authReducer,
    transportBooking: transportBookingReducer,
    transportCharter: charterReducer,
    logisticsBooking: logisticsReducer,
    settings: settingsReducer,
    [layoutAPISlice.reducerPath]: layoutAPISlice.reducer,
    [transportAPISlice.reducerPath]: transportAPISlice.reducer,
    [routesAPISlice.reducerPath]: routesAPISlice.reducer,
    [fleetsApiSlice.reducerPath]: fleetsApiSlice.reducer,
    [profileApiSlice.reducerPath]: profileApiSlice.reducer,
    [logisticsApiSlice.reducerPath]: logisticsApiSlice.reducer,
    [walletApiSlice.reducerPath]: walletApiSlice.reducer,
    [transactionsApi.reducerPath]: transactionsApi.reducer,
    [blogsApiSlice.reducerPath]: blogsApiSlice.reducer,
    [ordersApiSlice.reducerPath]: ordersApiSlice.reducer,
  });

  const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
        .prepend(sagaMiddleware)
        .concat([
          transportAPISlice.middleware,
          layoutAPISlice.middleware,
          routesAPISlice.middleware,
          fleetsApiSlice.middleware,
          profileApiSlice.middleware,
          logisticsApiSlice.middleware,
          walletApiSlice.middleware,
          transactionsApi.middleware,
          blogsApiSlice.middleware,
          ordersApiSlice.middleware,
        ])
        .concat(createStateSyncMiddleware(stateSyncConfig)),
    devTools: process.env.NODE_ENV !== 'production',
    preloadedState: initialState,
  });

  initMessageListener(store);
  sagaMiddleware.run(rootSaga);
  const persistor = persistStore(store);
  return { store, persistor };
};

export const { store, persistor } = configureStoreWithMiddlewares();
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
